<script setup lang="ts">
interface RelatedPage {
  slug: string
  jobTitle: string
  location: string
  searchVolume: number
}

const props = defineProps<{
  /** State code for state pages (e.g., "TX") */
  state?: string | null
  /** City slug for city+title pages (e.g., "dallas-tx") */
  city?: string | null
  /** Location display name for the title */
  locationName: string
  /** Whether this is a state-level page */
  isStatePage?: boolean
  /** Current page slug to exclude from results */
  currentSlug?: string | null
}>()

const dataKey = computed(() => `related-salary-${props.city || props.state || 'none'}`)

const queryParams = computed(() => {
  if (props.city) {
    return { city: props.city, limit: 100 }
  } else if (props.state) {
    return { state: props.state, limit: 100 }
  }
  return null
})

const { data } = await useAsyncData(
  dataKey,
  async () => {
    if (!queryParams.value) return { pages: [], total: 0 }
    return await $fetch<{ pages: RelatedPage[]; total: number }>('/api/tax/related-salary-pages', {
      query: queryParams.value
    })
  },
  {
    default: () => ({ pages: [], total: 0 }),
    watch: [() => props.city, () => props.state]
  }
)

// Shuffle array using Fisher-Yates algorithm with a seed (LCG for consistent results)
const shuffleArraySeeded = <T>(array: T[], seed: number): T[] => {
  const shuffled = [...array]
  let currentSeed = seed
  for (let i = shuffled.length - 1; i > 0; i--) {
    currentSeed = (currentSeed * 9301 + 49297) % 233280
    const j = Math.floor((currentSeed / 233280) * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

// Generate a consistent seed from the location string
const shuffleSeed = computed(() => {
  const str = props.city || props.state || 'default'
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
})

// Filter out the current page, shuffle with consistent seed, and take 12
const filteredPages = computed(() => {
  if (!data.value?.pages) return []
  const filtered = props.currentSlug
    ? data.value.pages.filter(page => page.slug.toLowerCase() !== props.currentSlug?.toLowerCase())
    : data.value.pages
  return shuffleArraySeeded(filtered, shuffleSeed.value).slice(0, 12)
})

const title = computed(() => {
  if (props.isStatePage) {
    return `Salaries in ${props.locationName}`
  }
  return `Other Salaries in ${props.locationName}`
})
</script>

<template>
  <section v-if="filteredPages.length > 0" class="mt-8 bg-white rounded-lg shadow-sm p-6">
    <h2 class="text-xl font-semibold text-gray-900 mb-4">
      {{ title }}
    </h2>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <NuxtLink
        v-for="page in filteredPages"
        :key="page.slug"
        :to="`/salary-calculator/${page.slug}`"
        class="flex items-center gap-2 p-3 rounded-lg border border-gray-200 hover:border-violet-300 hover:bg-violet-50 transition-colors"
      >
        <Icon name="i-heroicons-banknotes" class="w-5 h-5 text-violet-500 flex-shrink-0" />
        <span class="text-sm text-gray-700 truncate">{{ page.jobTitle }}</span>
      </NuxtLink>
    </div>
  </section>
</template>
